<script setup lang="ts">
import { useJobFormModal } from '../useJobFormModal';
import { getJobDisplayName } from '../utils';

const {
  jobFormModal,
  form,
  paymentDueMode,
  saving,
  generatingDocuments,
  deleting,
  syncingCalendar,
  sendingLine,
  error,
  success,
  documentLinks,
  documentHistoryLinks,
  showDocumentHistory,
  receiptPaymentId,
  quotationPaymentSelectionId,
  quotationPaymentChoices,
  receiptPayments,
  legacyPaymentReviewRequired,
  legacyReceivedDraft,
  confirmLegacyReceived,
  documentsFolderUrl,
  isEdit,
  configData,
  clientTemplates,
  companyDetailTemplates,
  selectedCompanyTemplateIndex,
  taxPercent,
  autoPaymentDuePreview,
  quotedTotal,
  quotedGrossTotal,
  eventDateChips,
  removeEventDate,
  workingTimePresets,
  timePicker,
  jobFormReminderSettings,
  isUploadingSlip,
  isRemovingSlip,
  slipUploadError,
  slipDragActive,
  hasSlipAttachment,
  canAddMoreSlips,
  slipItems,
  slipUploadProgress,
  SLIP_MAX_FILE_SIZE_LABEL,
  isQuotationPreviewOpen,
  quotationPreviewHtml,
  locationMapFavorites,
  currentMapIsFavorite,
  showLocationMapFavoriteEditor,
  savingLocationMapFavorite,
  locationMapFavoriteDraft,
  locationMapFavoriteError,
  openLocationMapFavoriteEditor,
  cancelLocationMapFavoriteEditor,
  saveLocationMapFavorite,
  applyLocationMapFavorite,
  toggleLocationMapFavorite,
  getWorkingTimeInputPart,
  formatPaymentDate,
  formatCurrency,
  submit,
  saveDraft,
  deleteCurrentJob,
  syncCalendarNow,
  sendLineNow,
  generateDocumentsNow,
  resetForm,
  handleClose,
  pickClientTemplate,
  applyCompanyDetailTemplate,
  clearQuotationCompanyDetail,
  openFormQuotationPreview,
  closeFormQuotationPreview,
  handleSlipUpload,
  openSlipFilePicker,
  onSlipDragOver,
  onSlipDragLeave,
  onSlipDrop,
  removeSlipAt,
  SLIP_FILE_ACCEPT,
  SLIP_SUPPORTED_FORMATS_LABEL,
  goToCalendarSettings,
  getGlobalCalendarRemindersSummary,
  openTimePicker,
  closeTimePicker,
  handleWorkingTimeBlur,
  onWorkingTimeInput,
  timePickerHourValues,
  timePickerMinuteValues,
  nudgeTimePicker,
  handleTimePickerWheel,
  selectTimePickerValue,
  applyTimePicker,
  applyWorkingTimePreset,
  onJobFormReminderUseDefaultChange,
  addJobFormReminder,
  removeJobFormReminder,
  syncPaymentDueModeToForm,
  finalInstallment,
  googleCalendarEventColors,
  selectedCalendarColorLabel,
  depositSystemEnabled,
  depositFeatureAvailable,
  paymentLedger,
  paymentSummary,
  paymentEntryDraft,
  paymentEntryError,
  paymentEntrySuggestedAmount,
  isUploadingPaymentSlip,
  paymentSlipFileName,
  paymentHistoryRows,
  voidedPaymentHistoryRows,
  finalBalancePayments,
  finalBalanceReceivedTotal,
  finalBalanceReceivedDate,
  otherUnassignedPaymentTotal,
  canReceiveFinalBalance,
  getSimpleInstallmentLabel,
  getInstallmentReceivedDate,
  getInstallmentLabel,
  openPaymentEntry,
  recordPaymentEntry,
  handlePaymentSlipUpload,
  voidPaymentEntry,
  depositInstallments,
  setInstallmentDueDateMode,
  setInstallmentDueDate,
  depositPlannedTotal,
  depositExtraTotal,
  contractTotalDue,
  depositPaidTotal,
  depositBalanceTotal,
  getDocumentsForInstallment,
  addDepositInstallment,
  removeDepositInstallment,
  sameTimeForAllDays,
  eventDayTimes,
  eventDayTimeRows,
  onEventDayTimeInput,
  onTimeModeChange,
  setAllDayWorkingTime,
  normalizeEventDayTime,
} = useJobFormModal();
</script>

<template>
  <div v-if="jobFormModal.isOpen" class="modal-overlay-center z-50">
    <div class="absolute inset-0 bg-slate-900/40 backdrop-blur-sm" @click="handleClose"></div>
    <div
      class="relative bg-white rounded-none md:rounded-2xl shadow-xl w-full h-[100dvh] md:h-auto md:max-w-3xl md:max-h-[min(92vh,900px)] flex flex-col overflow-hidden confirm-modal-panel"
      @click.stop
    >
      <div class="h-1.5 bg-gradient-to-r from-primary via-slate-700 to-slate-400 flex-shrink-0"></div>
      <div class="flex items-center justify-between p-3 md:p-5 border-b border-slate-100 bg-slate-50/50 flex-shrink-0">
        <div class="min-w-0">
          <h3 class="text-lg font-bold text-slate-800 truncate">
            {{ isEdit ? 'แก้ไขงาน' : jobFormModal.prefillJob ? 'กู้คืนงานจาก Google Calendar' : 'สร้างงานใหม่' }}
            <span v-if="form.JobID" class="text-sm font-normal text-slate-400 ml-2">{{ form.JobID }}</span>
          </h3>
          <p v-if="getJobDisplayName(form)" class="text-sm text-slate-500 truncate mt-0.5">
            {{ getJobDisplayName(form) }}
          </p>
        </div>
        <button
          type="button"
          @click="handleClose"
          :disabled="saving"
          :title="saving ? (generatingDocuments ? 'กำลังสร้างเอกสาร — กรุณารอสักครู่' : 'กำลังบันทึก — กรุณารอสักครู่') : ''"
          class="flex-shrink-0 text-slate-400 hover:text-slate-600 p-1 rounded-md hover:bg-slate-100 transition-colors ml-2 disabled:opacity-30 disabled:cursor-not-allowed"
        >
          <i class="ph ph-x text-xl"></i>
        </button>
      </div>

      <div data-job-form-scroll class="p-3 md:p-6 overflow-y-auto overflow-x-hidden overscroll-contain custom-scrollbar flex-1">
        <div v-if="error" data-validation-anchor="error-banner" data-validation-focus="error-banner" tabindex="-1" class="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-700 mb-4 whitespace-pre-line">
          {{ error }}
        </div>
        <div
          v-if="success"
          class="rounded-lg bg-emerald-50 border border-emerald-200 p-3 text-sm text-emerald-700 mb-4"
        >
          {{ success }}
        </div>
        <form id="jobFormModal" @submit.prevent="submit" class="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-5">
          <!-- Client -->
          <div class="md:col-span-2">
            <label class="block text-sm font-medium text-slate-700 mb-1">
              ลูกค้า <span class="text-red-500">*</span>
            </label>
            <input
              v-model="form.Client"
              required
              placeholder="ชื่อบริษัท / ชื่อในใบเสนอราคา"
              class="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
            <div v-if="clientTemplates.length > 0" class="mt-2 flex flex-wrap gap-1.5">
              <span class="text-xs text-slate-400 self-center">ลูกค้าบันทึกไว้:</span>
              <button
                v-for="(c, i) in clientTemplates"
                :key="i"
                type="button"
                @click="pickClientTemplate(c)"
                class="px-2.5 py-1 text-xs bg-slate-100 hover:bg-slate-200 text-slate-700 border border-slate-200 rounded-full transition-colors flex items-center gap-1"
              >
                <i class="ph ph-user text-xs"></i>{{ c.label || c.clientName }}
              </button>
            </div>
          </div>

          <!-- Contact Name (LINE only) -->
          <div class="md:col-span-2">
            <label class="block text-sm font-medium text-slate-700 mb-1">ชื่อเรียก (LINE)</label>
            <input
              v-model="form['Contact Name']"
              placeholder="ชื่อที่ต้องการให้แสดง เช่น ข่มขลัง / IQIYI"
              class="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
            <p class="text-[11px] text-slate-400 mt-1">
              ใช้ในข้อความ LINE เท่านั้น — ไม่แสดงในใบเสนอราคาและเอกสาร
            </p>
          </div>

          <!-- Job Type (matches sheet: ลูกค้า → ชื่อเรียก → เบอร์โทร → ประเภทงาน → ชื่องาน) -->
          <div class="md:col-span-2">
            <label class="block text-sm font-medium text-slate-700 mb-1">ประเภทงาน</label>
            <input
              v-model="form.JobType"
              placeholder="เช่น วันรับจริง, วันซ้อม, พิธีเช้า, Event"
              class="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
            <label class="mt-2 inline-flex items-center gap-2 text-sm text-slate-700 select-none">
              <input
                type="checkbox"
                v-model="form.PersonalUse"
                class="rounded border-slate-300 text-primary focus:ring-primary"
              />
              ใช้งานส่วนตัว (ไม่คิดเงิน / ไม่เตือน / ไม่นับสถานะชำระ)
            </label>
          </div>

          <!-- Job Name -->
          <div class="md:col-span-2">
            <label class="block text-sm font-medium text-slate-700 mb-1">ชื่องาน</label>
            <input
              v-model="form['Job Name']"
              placeholder="เช่น งานแต่งงาน คุณเอ - คุณบี"
              class="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
          </div>

          <!-- Event Date -->
          <div data-validation-anchor="event-date" tabindex="-1">
            <label class="block text-sm font-medium text-slate-700 mb-1">
              วันที่งาน <span class="text-red-500">*</span>
            </label>
            <input
              id="eventDateRangeInput"
              data-validation-focus="event-date"
              type="text"
              readonly
              placeholder="ลากเลือกช่วงวันที่นี่"
              class="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
            <input type="hidden" v-model="form['Event Date']" required />

            <input
              id="eventDateMultiInput"
              type="text"
              readonly
              placeholder="คลิกเพื่อจิ้มเลือกวันเดี่ยว/หลายวัน"
              class="w-full mt-2 bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />

            <div v-if="eventDateChips.length" class="flex flex-wrap gap-1.5 mt-2">
              <span
                v-for="chip in eventDateChips"
                :key="chip.value"
                class="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200"
              >
                {{ chip.label }}
                <button
                  type="button"
                  @click="removeEventDate(chip.value)"
                  class="w-4 h-4 flex items-center justify-center rounded-full hover:bg-blue-200"
                  :title="'ลบวันนี้ออก'"
                >
                  <i class="ph ph-x text-[10px]"></i>
                </button>
              </span>
            </div>

            <p class="text-xs text-slate-400 mt-1">
              ลากช่วงวันด้านบน (เช่น 1-3) หรือคลิกปฏิทินด้านล่างแล้วจิ้มเลือกวันข้ามวันได้เลย (เช่น 5, 10) — รวมกันได้ในงานเดียว
            </p>
          </div>

          <!-- Working Time -->
          <div data-validation-anchor="working-time" tabindex="-1">
            <label class="block text-sm font-medium text-slate-700 mb-1">
              เวลางาน
            </label>
            <div v-if="eventDayTimeRows.length > 1" class="mb-3 p-3 rounded-lg border border-slate-200 bg-slate-50">
              <p class="text-xs font-medium text-slate-700 mb-2">กำหนดเวลาเมื่อมีหลายวัน</p>
              <div class="flex flex-wrap gap-4 text-sm text-slate-700">
                <label class="inline-flex items-center gap-2 cursor-pointer">
                  <input type="radio" v-model="sameTimeForAllDays" :value="true" @change="onTimeModeChange" class="text-primary focus:ring-primary" />
                  ใช้เวลาเดียวกันทุกวัน
                </label>
                <label class="inline-flex items-center gap-2 cursor-pointer">
                  <input type="radio" v-model="sameTimeForAllDays" :value="false" @change="onTimeModeChange" class="text-primary focus:ring-primary" />
                  กำหนดเวลาแยกแต่ละวัน
                </label>
              </div>
            </div>
            <p v-if="eventDayTimeRows.length > 1 && sameTimeForAllDays" class="mb-2 text-[11px] text-slate-500">
              เวลานี้จะใช้เหมือนกันกับทุกวันที่เลือก
            </p>
            <p v-else-if="eventDayTimeRows.length > 1 && !sameTimeForAllDays" class="mb-2 text-[11px] text-blue-600">
              เปิดโหมดแยกวันแล้ว — ตั้งเวลาในแต่ละวันที่ด้านล่าง ช่องเวลารวมถูกซ่อนไว้เพื่อไม่ให้สับสน
            </p>
            <button
              type="button"
              @click="setAllDayWorkingTime"
              class="mb-2 inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100"
            >
              <i class="ph ph-sun"></i> ทั้งวัน (00:00 - 23:59)
            </button>
            <div v-if="eventDayTimeRows.length <= 1 || sameTimeForAllDays" class="grid grid-cols-2 gap-2">
              <div class="time-picker-root relative">
                <div class="text-xs text-slate-500 mb-1">เริ่ม</div>
                <div class="relative">
                  <input
                    type="text"
                    data-validation-focus="working-time"
                    inputmode="numeric"
                    placeholder="09:00"
                    :value="getWorkingTimeInputPart(form['Working Time'], 'start')"
                    @focus="openTimePicker('start')"
                    @click="openTimePicker('start')"
                    @input="onWorkingTimeInput('start', ($event.target as HTMLInputElement).value)"
                    @blur="handleWorkingTimeBlur"
                    @keydown.escape="closeTimePicker"
                    class="w-full bg-white border border-slate-300 rounded-lg pl-3 pr-9 py-2.5 text-sm font-mono focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                  <button
                    type="button"
                    @mousedown.prevent
                    @click="openTimePicker('start')"
                    class="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-md text-slate-400 hover:text-primary hover:bg-slate-100 flex items-center justify-center"
                  >
                    <i class="ph ph-clock text-base"></i>
                  </button>
                </div>
                <div
                  v-if="timePicker.open && timePicker.part === 'start'"
                  class="time-picker-popover"
                  @mousedown.prevent
                >
                  <div class="time-picker-wheel">
                    <div class="time-picker-column" @wheel.stop.prevent="handleTimePickerWheel($event, 'hour')">
                      <button type="button" class="time-picker-step" @click="nudgeTimePicker('hour', -1)">
                        <i class="ph ph-caret-up text-xs"></i>
                      </button>
                      <button
                        v-for="hour in timePickerHourValues()"
                        :key="'start-h-' + hour"
                        type="button"
                        @click="selectTimePickerValue('hour', hour)"
                        :class="['time-picker-option', hour === timePicker.hour ? 'is-selected' : '']"
                      >
                        {{ hour }}
                      </button>
                      <button type="button" class="time-picker-step" @click="nudgeTimePicker('hour', 1)">
                        <i class="ph ph-caret-down text-xs"></i>
                      </button>
                    </div>
                    <div class="text-center text-base font-semibold text-slate-400">:</div>
                    <div class="time-picker-column" @wheel.stop.prevent="handleTimePickerWheel($event, 'minute')">
                      <button type="button" class="time-picker-step" @click="nudgeTimePicker('minute', -1)">
                        <i class="ph ph-caret-up text-xs"></i>
                      </button>
                      <button
                        v-for="minute in timePickerMinuteValues()"
                        :key="'start-m-' + minute"
                        type="button"
                        @click="selectTimePickerValue('minute', minute)"
                        :class="['time-picker-option', minute === timePicker.minute ? 'is-selected' : '']"
                      >
                        {{ minute }}
                      </button>
                      <button type="button" class="time-picker-step" @click="nudgeTimePicker('minute', 1)">
                        <i class="ph ph-caret-down text-xs"></i>
                      </button>
                    </div>
                  </div>
                  <div class="border-t border-slate-100 p-2">
                    <button
                      type="button"
                      @click="applyTimePicker"
                      class="w-full bg-slate-800 hover:bg-slate-900 text-white rounded-md py-1.5 text-xs font-semibold"
                    >
                      ยืนยัน
                    </button>
                  </div>
                </div>
              </div>
              <div class="time-picker-root relative">
                <div class="text-xs text-slate-500 mb-1">สิ้นสุด</div>
                <div class="relative">
                  <input
                    type="text"
                    inputmode="numeric"
                    placeholder="17:00"
                    :value="getWorkingTimeInputPart(form['Working Time'], 'end')"
                    @focus="openTimePicker('end')"
                    @click="openTimePicker('end')"
                    @input="onWorkingTimeInput('end', ($event.target as HTMLInputElement).value)"
                    @blur="handleWorkingTimeBlur"
                    @keydown.escape="closeTimePicker"
                    class="w-full bg-white border border-slate-300 rounded-lg pl-3 pr-9 py-2.5 text-sm font-mono focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                  />
                  <button
                    type="button"
                    @mousedown.prevent
                    @click="openTimePicker('end')"
                    class="absolute right-2 top-1/2 -translate-y-1/2 w-6 h-6 rounded-md text-slate-400 hover:text-primary hover:bg-slate-100 flex items-center justify-center"
                  >
                    <i class="ph ph-clock text-base"></i>
                  </button>
                </div>
                <div
                  v-if="timePicker.open && timePicker.part === 'end'"
                  class="time-picker-popover"
                  @mousedown.prevent
                >
                  <div class="time-picker-wheel">
                    <div class="time-picker-column" @wheel.stop.prevent="handleTimePickerWheel($event, 'hour')">
                      <button type="button" class="time-picker-step" @click="nudgeTimePicker('hour', -1)">
                        <i class="ph ph-caret-up text-xs"></i>
                      </button>
                      <button
                        v-for="hour in timePickerHourValues()"
                        :key="'end-h-' + hour"
                        type="button"
                        @click="selectTimePickerValue('hour', hour)"
                        :class="['time-picker-option', hour === timePicker.hour ? 'is-selected' : '']"
                      >
                        {{ hour }}
                      </button>
                      <button type="button" class="time-picker-step" @click="nudgeTimePicker('hour', 1)">
                        <i class="ph ph-caret-down text-xs"></i>
                      </button>
                    </div>
                    <div class="text-center text-base font-semibold text-slate-400">:</div>
                    <div class="time-picker-column" @wheel.stop.prevent="handleTimePickerWheel($event, 'minute')">
                      <button type="button" class="time-picker-step" @click="nudgeTimePicker('minute', -1)">
                        <i class="ph ph-caret-up text-xs"></i>
                      </button>
                      <button
                        v-for="minute in timePickerMinuteValues()"
                        :key="'end-m-' + minute"
                        type="button"
                        @click="selectTimePickerValue('minute', minute)"
                        :class="['time-picker-option', minute === timePicker.minute ? 'is-selected' : '']"
                      >
                        {{ minute }}
                      </button>
                      <button type="button" class="time-picker-step" @click="nudgeTimePicker('minute', 1)">
                        <i class="ph ph-caret-down text-xs"></i>
                      </button>
                    </div>
                  </div>
                  <div class="border-t border-slate-100 p-2">
                    <button
                      type="button"
                      @click="applyTimePicker"
                      class="w-full bg-slate-800 hover:bg-slate-900 text-white rounded-md py-1.5 text-xs font-semibold"
                    >
                      ยืนยัน
                    </button>
                  </div>
                </div>
              </div>
            </div>
            <div v-if="eventDayTimeRows.length <= 1 || sameTimeForAllDays" class="mt-2 flex flex-wrap gap-1.5">
              <button
                v-for="preset in workingTimePresets"
                :key="preset.key"
                type="button"
                @click="applyWorkingTimePreset(preset)"
                class="px-2.5 py-1 text-[11px] font-medium text-slate-600 bg-slate-50 border border-slate-200 rounded-md hover:bg-slate-100 hover:text-slate-800 transition-colors"
              >
                {{ preset.label }} · {{ preset.start }}-{{ preset.end }}
              </button>
            </div>
            <div v-if="eventDayTimeRows.length > 1 && !sameTimeForAllDays" class="mt-4 space-y-2">
              <p class="text-xs font-semibold text-slate-700">เวลาแยกตามวัน</p>
              <div
                v-for="row in eventDayTimeRows"
                :key="'event-day-time-' + row.key"
                class="flex items-center gap-2 p-2.5 rounded-lg border border-slate-200 bg-white"
              >
                <span class="text-xs text-slate-600 min-w-[110px]">{{ row.label }}</span>
                <input
                  type="text"
                  data-validation-focus="working-time"
                  inputmode="numeric"
                  :value="eventDayTimes[row.key] || ''"
                  placeholder="09:00 - 17:00"
                  @input="onEventDayTimeInput(row.key, ($event.target as HTMLInputElement).value)"
                  @blur="normalizeEventDayTime(row.key)"
                  class="flex-1 bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                />
              </div>
            </div>
          </div>

          <!-- Calendar Reminders -->
          <div class="md:col-span-2 p-4 rounded-xl border border-blue-100 bg-blue-50/40 space-y-3">
            <div class="flex flex-wrap items-start justify-between gap-2">
              <div>
                <label class="block text-sm font-semibold text-slate-800">
                  <i class="ph ph-bell-ringing text-primary mr-1"></i> แจ้งเตือน Google Calendar
                </label>
                <p class="text-xs text-slate-500 mt-0.5">งานจะ sync ไป Calendar · เด้งมือถือตามที่ตั้งด้านล่าง</p>
              </div>
              <button
                type="button"
                @click="goToCalendarSettings"
                class="text-xs text-primary hover:text-slate-800 underline underline-offset-2"
              >
                ตั้งค่าเริ่มต้นทั้งระบบ
              </button>
            </div>

            <div>
              <p class="text-xs font-medium text-slate-700 mb-2">
                <i class="ph ph-palette text-primary mr-1"></i> สีใน Google Calendar
              </p>
              <div class="flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  title="สีเริ่มต้นของปฏิทิน"
                  :aria-pressed="!form['Color']"
                  class="w-7 h-7 rounded-full border-2 border-dashed border-slate-300 bg-white hover:border-slate-400 transition-shadow"
                  :class="!form['Color'] ? 'ring-2 ring-offset-1 ring-primary shadow-sm' : ''"
                  @click="form['Color'] = ''"
                >
                  <span class="sr-only">สีเริ่มต้น</span>
                </button>
                <button
                  v-for="color in googleCalendarEventColors"
                  :key="color.id"
                  type="button"
                  :title="color.nameTh + ' (' + color.name + ')'"
                  :aria-label="color.nameTh"
                  :aria-pressed="form['Color'] === color.id"
                  class="w-7 h-7 rounded-full border border-black/10 hover:scale-105 transition-transform"
                  :class="form['Color'] === color.id ? 'ring-2 ring-offset-1 ring-primary shadow-sm' : ''"
                  :style="{ backgroundColor: color.background }"
                  @click="form['Color'] = color.id"
                />
              </div>
              <p class="text-[11px] text-slate-500 mt-1.5">
                {{ selectedCalendarColorLabel }}
              </p>
            </div>

            <label class="inline-flex items-center gap-2 text-sm text-slate-700">
              <input
                type="checkbox"
                v-model="jobFormReminderSettings.useDefault"
                @change="onJobFormReminderUseDefaultChange"
                class="rounded border-slate-300 text-primary focus:ring-primary"
              />
              ใช้ค่าเริ่มต้นจาก Settings
              <span class="text-xs text-slate-500">({{ getGlobalCalendarRemindersSummary() }})</span>
            </label>
            <div v-if="!jobFormReminderSettings.useDefault" class="space-y-2">
              <div
                v-if="jobFormReminderSettings.reminders.length === 0"
                class="text-sm text-slate-400 border border-dashed border-slate-200 rounded-lg px-4 py-4 text-center bg-white"
              >
                ยังไม่มีการแจ้งเตือน — กดเพิ่มด้านล่าง
              </div>
              <div
                v-for="(rem, i) in jobFormReminderSettings.reminders"
                :key="'job-rem-' + i"
                class="flex flex-wrap items-center gap-2 p-3 rounded-lg border border-slate-200 bg-white"
              >
                <select
                  v-model="rem.method"
                  @change="onJobFormReminderUseDefaultChange"
                  class="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm min-w-[150px]"
                >
                  <option value="popup">แจ้งเตือน (มือถือ)</option>
                  <option value="email">อีเมล</option>
                </select>
                <input
                  type="number"
                  v-model.number="rem.value"
                  min="1"
                  max="9999"
                  @change="onJobFormReminderUseDefaultChange"
                  class="w-16 bg-white border border-slate-200 rounded-lg px-2 py-2 text-sm text-center"
                />
                <select
                  v-model="rem.unit"
                  @change="onJobFormReminderUseDefaultChange"
                  class="bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm"
                >
                  <option value="minutes">นาที</option>
                  <option value="hours">ชั่วโมง</option>
                  <option value="days">วัน</option>
                  <option value="weeks">สัปดาห์</option>
                </select>
                <span class="text-sm text-slate-600">ก่อนงาน</span>
                <button
                  type="button"
                  @click="removeJobFormReminder(i)"
                  class="ml-auto w-8 h-8 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-600 flex items-center justify-center"
                >
                  <i class="ph ph-trash"></i>
                </button>
              </div>
              <button
                type="button"
                @click="addJobFormReminder"
                class="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-white border border-slate-300 text-slate-700 hover:bg-slate-50"
              >
                <i class="ph ph-plus"></i> เพิ่มการแจ้งเตือน
              </button>
            </div>
          </div>

          <!-- Job Title -->
          <div class="md:col-span-2">
            <label class="block text-sm font-medium text-slate-700 mb-1">
              รายการ / ชื่องาน <span class="text-red-500">*</span>
            </label>
            <textarea
              v-model="form['Item / Job Title']"
              required
              rows="3"
              placeholder="เช่น 1. งานบวงสรวง Central World"
              class="w-full max-h-56 overflow-y-auto bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary custom-scrollbar job-content-text"
            ></textarea>
          </div>

          <!-- Quotation Description -->
          <div class="md:col-span-2">
            <label class="block text-sm font-medium text-slate-700 mb-1">รายละเอียดงานสำหรับใบเสนอราคา</label>
            <textarea
              v-model="form['Quotation Description']"
              rows="4"
              placeholder="- ถ่ายภาพบรรยากาศงาน Event ต่างๆ"
              class="w-full max-h-56 overflow-y-auto bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary custom-scrollbar job-content-text"
            ></textarea>
          </div>

          <!-- Quotation Company Detail -->
          <div class="md:col-span-2">
            <label class="block text-sm font-medium text-slate-700 mb-1">
              ข้อมูลบริษัทสำหรับใบเสนอราคา (ว่าง = ใช้ default)
            </label>
            <div v-if="companyDetailTemplates.length > 0" class="mb-2 flex flex-col sm:flex-row gap-2 sm:items-center">
              <select
                v-model="selectedCompanyTemplateIndex"
                @change="applyCompanyDetailTemplate"
                class="w-full sm:max-w-xs bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              >
                <option value="">เลือก Template ข้อมูลบริษัท</option>
                <option v-for="item in companyDetailTemplates" :key="item.index" :value="String(item.index)">
                  {{ item.label }}
                </option>
              </select>
              <button
                type="button"
                @click="clearQuotationCompanyDetail"
                class="px-3 py-2 text-xs font-medium text-slate-600 bg-slate-50 border border-slate-200 rounded-lg hover:bg-slate-100 transition-colors"
              >
                ใช้ค่า default
              </button>
            </div>
            <textarea
              v-model="form['Quotation Company Detail']"
              rows="3"
              placeholder="เช่น ชื่อบริษัท, ที่อยู่, เลขผู้เสียภาษี"
              class="w-full max-h-56 overflow-y-auto bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary custom-scrollbar job-content-text"
            ></textarea>
          </div>

          <!-- Price -->
          <div>
            <label class="block text-sm font-medium text-slate-700 mb-1">ราคา (THB)</label>
            <input
              type="number"
              v-model="form['Price per Unit']"
              placeholder="0.00"
              class="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
            <p v-if="form['Apply Tax']" class="text-xs text-slate-500 mt-1">
              ยอดสุทธิที่ต้องการรับจริง · ยอดก่อนหัก {{ taxPercent }}%: {{ formatCurrency(quotedGrossTotal) }}
            </p>
          </div>
          <div v-if="form['Apply Tax']">
            <label class="block text-sm font-medium text-blue-700 mb-1">ยอดก่อนหักภาษี {{ taxPercent }}%</label>
            <input
              type="text"
              :value="formatCurrency(quotedGrossTotal)"
              readonly
              class="w-full bg-blue-50 border border-blue-200 rounded-lg px-4 py-2.5 text-sm text-blue-800 cursor-not-allowed"
            />
          </div>

          <!-- Tax & Signature -->
          <div class="md:col-span-2 flex flex-col sm:flex-row flex-wrap gap-4 p-3 bg-slate-50 border border-slate-200 rounded-lg">
            <label class="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input type="checkbox" v-model="form['Apply Tax']" class="rounded border-slate-300 text-primary focus:ring-primary" />
              หักภาษี ณ ที่จ่าย {{ taxPercent }}%
            </label>
            <label class="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                v-model="form['Include Signature']"
                class="rounded border-slate-300 text-primary focus:ring-primary"
              />
              ใส่ลายเซ็นในใบเสนอราคา
            </label>
            <label class="inline-flex items-center gap-2 text-sm text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                v-model="form['Show Provider Name on Quotation']"
                class="rounded border-slate-300 text-primary focus:ring-primary"
              />
              แสดงชื่อ-นามสกุลด้านบนใบเสนอราคา
            </label>
          </div>

          <!-- Optional deposit schedule and actual payment tracking -->
          <div class="md:col-span-2">
            <div v-if="depositFeatureAvailable && depositSystemEnabled" data-validation-anchor="deposit-schedule" tabindex="-1" class="rounded-xl border border-amber-200 bg-amber-50/50 p-4 space-y-4">
              <div class="flex flex-wrap items-start justify-between gap-2">
                <div>
                  <label class="block text-sm font-semibold text-slate-800">ระบบมัดจำ <span class="ml-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-800">เปิดอยู่</span></label>
                  <p class="text-xs text-slate-500 mt-1">กรอกยอดมัดจำและวันครบกำหนด; รับเงินจริงและวันที่รับให้บันทึกเมื่อได้รับเงินแล้ว</p>
                </div>
                <button type="button" :disabled="saving || paymentEntryDraft.open" @click="form['Deposit Enabled'] = false" :aria-pressed="depositSystemEnabled" class="text-xs rounded-full px-3 py-2 border border-slate-300 bg-white text-slate-600 disabled:opacity-50">ปิดมัดจำ · กลับไปแบบเดิม</button>
              </div>

              <div v-if="legacyPaymentReviewRequired" data-validation-anchor="legacy-payment-review" tabindex="-1" class="rounded-lg border border-amber-300 bg-amber-100 p-3 space-y-2">
                <p class="text-sm text-amber-900">งานเดิมยังไม่มีประวัติรับเงินจริงแยกจากแผนงวด กรุณาตรวจยอดเงินจริงสะสมก่อนใช้ระบบนี้</p>
                <label class="block text-xs text-amber-900">ยอดเงินจริงที่ได้รับมาแล้วรวมทุกงวด (0 หากยังไม่เคยรับเงิน)<input v-model="legacyReceivedDraft" data-validation-focus="legacy-payment-review" type="number" min="0" step="0.01" class="mt-1 w-full rounded border border-amber-300 bg-white px-3 py-2 text-sm" /></label>
                <button type="button" @click="confirmLegacyReceived" class="rounded-lg border border-amber-400 bg-white px-3 py-2 text-sm text-amber-900">ยืนยันยอดรับเงินจริงเดิม</button>
              </div>

              <div class="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-2 text-sm">
                <div class="rounded-lg bg-white border border-slate-200 px-3 py-2.5"><span class="text-slate-500">ยอดงานก่อนคิด 3%</span><strong class="block text-slate-800 text-lg">{{ formatCurrency(quotedTotal) }} บาท</strong></div>
                <div class="rounded-lg bg-slate-800 px-3 py-2.5 text-white"><span class="text-xs text-slate-300">ยอดเรียกเก็บรวม</span><strong class="mt-0.5 block text-lg">{{ formatCurrency(contractTotalDue) }} บาท</strong><span v-if="depositExtraTotal > 0" class="block text-xs text-slate-300">รวมเพิ่มครั้งเดียว {{ formatCurrency(depositExtraTotal) }} บาท</span></div>
                <div class="rounded-lg bg-white border border-emerald-200 px-3 py-2.5"><span class="text-slate-500">รับเงินจริงแล้ว</span><strong class="block text-emerald-700 text-lg">{{ formatCurrency(depositPaidTotal) }} บาท</strong></div>
                <div class="rounded-lg bg-white border border-blue-200 px-3 py-2.5"><span class="text-slate-500">คงเหลือที่ต้องชำระ</span><strong class="block text-blue-700 text-lg">{{ formatCurrency(depositBalanceTotal) }} บาท</strong></div>
              </div>
              <p v-if="depositExtraTotal > 0" class="text-xs text-violet-700">คิดเพิ่ม {{ taxPercent }}% จากยอดงานทั้งก้อนครั้งเดียว โดยรวมส่วนเพิ่มไว้ในยอดคงเหลือ — ไม่บวกซ้ำในมัดจำแต่ละรอบ</p>

              <div class="space-y-3">
                <div class="flex flex-wrap items-center justify-between gap-2"><div><strong class="text-sm text-slate-800">มัดจำแต่ละรอบ</strong><p class="text-xs text-slate-500">ส่วนเพิ่ม {{ taxPercent }}% คำนวณจากยอดงานรวมครั้งเดียว เมื่อเลือกหักภาษี ณ ที่จ่ายด้านบน; มัดจำแต่ละรอบไม่ต้องติ๊กเพิ่มเอง</p></div></div>
                <div v-if="depositInstallments.length === 0" class="rounded-lg border border-dashed border-amber-300 bg-white/70 px-3 py-3 text-sm text-slate-500">ยังไม่มีมัดจำที่กำหนด</div>
                <div v-for="(installment, index) in depositInstallments" :key="installment.id" class="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end p-3 rounded-lg border border-amber-100 bg-white">
                  <div class="sm:col-span-2 flex flex-wrap items-center justify-between gap-2"><strong class="text-sm text-slate-700">{{ getSimpleInstallmentLabel(installment, index) }}</strong><span class="text-xs rounded px-2 py-1" :class="paymentSummary.rounds[index]?.status === 'paid' ? 'bg-emerald-50 text-emerald-700' : paymentSummary.rounds[index]?.status === 'overdue' ? 'bg-red-50 text-red-700' : 'bg-slate-100 text-slate-600'">{{ paymentSummary.rounds[index]?.status === 'paid' ? 'รับครบแล้ว' : paymentSummary.rounds[index]?.status === 'overdue' ? 'เลยกำหนด' : paymentSummary.rounds[index]?.status === 'partial' ? 'ได้รับบางส่วน' : 'ยังไม่ได้รับ' }}</span></div>
                  <div><label class="block text-xs text-slate-500 mb-1">{{ installment.kind === 'final' ? 'ยอดที่ต้องชำระ (บาท)' : 'ยอดมัดจำ (บาท)' }}</label><input v-model.number="installment.amount" :data-validation-focus="`installment-amount-${index + 1}`" type="number" min="0" step="0.01" placeholder="0.00" class="w-full bg-white border border-slate-300 rounded-lg px-3 py-2 text-sm" /></div>
                  <div>
                    <label class="block text-xs text-slate-500 mb-1">กำหนดชำระวันที่</label>
                    <input v-model="installment.dueDate" @change="setInstallmentDueDate(index, ($event.target as HTMLInputElement).value)" :readonly="installment.dueDateMode === 'auto'" :data-validation-focus="`installment-due-date-${index + 1}`" type="date" :class="['w-full rounded-lg border border-slate-300 px-3 py-2 text-sm', installment.dueDateMode === 'auto' ? 'bg-slate-100 text-slate-600' : 'bg-white']" />
                    <label class="mt-2 inline-flex items-center gap-1.5 text-[11px] text-slate-500"><input type="checkbox" :checked="installment.dueDateMode === 'auto'" @change="setInstallmentDueDateMode(index, ($event.target as HTMLInputElement).checked ? 'auto' : 'manual')" class="rounded border-slate-300 text-primary focus:ring-primary" />คำนวณอัตโนมัติ (วันงาน + 1 เดือน)</label>
                  </div>
                  <div class="sm:col-span-2 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2.5">
                    <div class="text-xs text-slate-600"><span>ยอดที่ได้รับจริง <strong class="text-emerald-700">{{ formatCurrency(paymentSummary.rounds[index]?.received || 0) }} บาท</strong></span><span class="mx-2">·</span><span>วันที่ได้รับ {{ getInstallmentReceivedDate(installment.id || '') ? formatPaymentDate(getInstallmentReceivedDate(installment.id || '')) : 'ยังไม่ได้รับ' }}</span><span v-if="(paymentSummary.rounds[index]?.extraAmount || 0) > 0" class="ml-2 text-violet-700">(รวมส่วนเพิ่มจากยอดงาน {{ formatCurrency(paymentSummary.rounds[index]?.extraAmount || 0) }} บาท)</span></div>
                    <button type="button" @click="openPaymentEntry(installment.id)" :disabled="saving || (paymentSummary.rounds[index]?.outstanding || 0) <= 0 || legacyPaymentReviewRequired" class="inline-flex min-h-10 items-center gap-1.5 rounded-lg border border-emerald-300 bg-white px-3 py-2 text-xs font-semibold text-emerald-800 hover:bg-emerald-50 disabled:cursor-not-allowed disabled:opacity-50"><i class="ph ph-hand-coins"></i> {{ (paymentSummary.rounds[index]?.outstanding || 0) > 0 ? 'ใส่ยอดที่ได้รับจริง' : 'รับครบแล้ว' }}</button>
                  </div>
                  <details class="sm:col-span-2 rounded-lg border border-blue-100 bg-blue-50/60 p-3">
                    <summary class="cursor-pointer text-xs font-semibold text-blue-900">เอกสารและประวัติของรอบนี้</summary>
                    <div class="mt-2 space-y-2">
                    <strong class="block text-xs text-blue-900">ยอดตามแผน {{ formatCurrency(paymentSummary.rounds[index]?.grossAmount || installment.amount || 0) }} บาท</strong>
                    <div v-if="installment.id && getDocumentsForInstallment(installment.id).length" class="flex flex-wrap gap-2 pt-1">
                      <a v-for="doc in getDocumentsForInstallment(installment.id || '')" :key="doc.pdfUrl" :href="doc.pdfUrl" target="_blank" rel="noopener" class="inline-flex min-h-9 items-center rounded border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700">เปิด{{ doc.label }}{{ doc.number ? ` ${doc.number}` : '' }}</a>
                    </div>
                    <div v-if="paymentHistoryRows.some(entry => entry.installmentId === installment.id && !entry.voidedAt)" class="space-y-2 pt-1">
                      <div v-for="entry in paymentHistoryRows.filter(item => item.installmentId === installment.id && !item.voidedAt)" :key="`receipt-${entry.id}`" class="flex flex-wrap items-center gap-2 rounded-lg border border-emerald-100 bg-white px-3 py-2 text-xs">
                        <span class="mr-auto text-slate-600">รับ {{ formatCurrency(entry.amount) }} บาท · {{ formatPaymentDate(entry.paidDate) }}</span>
                      </div>
                    </div>
                    <p v-if="!getDocumentsForInstallment(installment.id || '').length" class="text-[11px] text-slate-500">ลิงก์เอกสารของ{{ getSimpleInstallmentLabel(installment, index) }}จะแสดงตรงนี้เมื่อสร้างแล้ว</p>
                    </div>
                  </details>
                  <div v-if="paymentEntryDraft.open && paymentEntryDraft.installmentId === installment.id" data-validation-anchor="payment-entry" tabindex="-1" class="sm:col-span-2 rounded-lg border border-emerald-200 bg-white p-3 space-y-3">
                    <div>
                      <strong class="text-sm text-emerald-800">บันทึกยอดที่ได้รับจริง — {{ getSimpleInstallmentLabel(installment, index) }}</strong>
                      <p class="text-xs text-slate-600">ยอดตามแผนรวมส่วนเพิ่ม {{ formatCurrency(paymentSummary.rounds[index]?.grossAmount || installment.amount) }} บาท · ค้างรับรอบนี้ {{ formatCurrency(paymentSummary.rounds[index]?.outstanding || 0) }} บาท · ครบกำหนด {{ formatPaymentDate(installment.dueDate) }}</p>
                      <p v-if="(paymentSummary.rounds[index]?.extraAmount || 0) > 0" class="mt-1 text-xs text-violet-700">ยอดรอบสุดท้ายรวมส่วนเพิ่มจากยอดงาน {{ formatCurrency(paymentSummary.rounds[index]?.extraAmount || 0) }} บาท</p>
                      <p class="mt-1 text-xs text-amber-700">กรอกยอดเงินจริงที่ได้รับ; ปุ่มเติมยอดด้านล่างเป็นตัวช่วยกรอกเท่านั้น</p>
                    </div>
                    <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <label class="text-xs text-slate-600">ยอดที่ได้รับจริง (บาท)<input v-model="paymentEntryDraft.amount" type="number" min="0.01" step="0.01" class="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm" /></label>
                        <button type="button" @click="paymentEntryDraft.amount = paymentEntrySuggestedAmount" class="mt-1 text-xs font-medium text-blue-700 underline underline-offset-2">เติมยอดค้างตามแผน {{ formatCurrency(paymentEntrySuggestedAmount) }} บาท</button>
                      </div>
                      <label class="text-xs text-slate-600">วันที่ได้รับเงินจริง<input v-model="paymentEntryDraft.paidDate" type="date" class="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm" /></label>
                      <div class="sm:col-span-2 text-xs text-slate-600">
                        <span class="block mb-1">สลิป (ถ้ามี)</span>
                        <label :for="`payment-slip-file-input-${installment.id}`" class="relative inline-flex min-h-10 items-center gap-2 overflow-hidden rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 cursor-pointer hover:border-primary hover:bg-blue-50 focus-within:ring-2 focus-within:ring-primary/30" :class="isUploadingPaymentSlip ? 'pointer-events-none opacity-60' : ''">
                          <i :class="isUploadingPaymentSlip ? 'ph ph-spinner animate-spin' : 'ph ph-upload-simple'"></i>
                          {{ isUploadingPaymentSlip ? 'กำลังอัปโหลด...' : paymentSlipFileName ? 'เปลี่ยนไฟล์สลิป' : 'อัปโหลดสลิป' }}
                          <input :id="`payment-slip-file-input-${installment.id}`" type="file" :accept="SLIP_FILE_ACCEPT" :disabled="isUploadingPaymentSlip || saving" aria-label="อัปโหลดสลิป" class="absolute inset-0 h-full w-full cursor-pointer opacity-0" @change="handlePaymentSlipUpload" />
                        </label>
                        <span class="ml-2 text-slate-400">{{ SLIP_SUPPORTED_FORMATS_LABEL }} · {{ SLIP_MAX_FILE_SIZE_LABEL }}</span>
                        <div v-if="paymentEntryDraft.slipUrl" class="mt-2 flex flex-wrap items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-emerald-800">
                          <i class="ph ph-check-circle"></i><span class="min-w-0 flex-1 truncate">{{ paymentSlipFileName || 'อัปโหลดหลักฐานแล้ว' }}</span>
                          <a :href="paymentEntryDraft.slipUrl" target="_blank" rel="noopener" class="shrink-0 underline underline-offset-2">เปิดไฟล์ที่อัปโหลด</a>
                        </div>
                      </div>
                    </div>
                    <p v-if="paymentEntryError" role="alert" class="text-sm text-red-600">{{ paymentEntryError }}</p>
                    <div class="flex flex-wrap gap-2"><button type="button" data-validation-focus="payment-entry" @click="recordPaymentEntry" :disabled="isUploadingPaymentSlip" class="rounded-lg bg-emerald-600 px-3 py-2 text-sm text-white disabled:opacity-50">บันทึกยอดที่ได้รับจริง</button><button type="button" @click="paymentEntryDraft.open = false" :disabled="isUploadingPaymentSlip" class="rounded-lg border border-slate-200 px-3 py-2 text-sm disabled:opacity-50">ยกเลิก</button></div>
                  </div>
                  <details v-if="paymentHistoryRows.some(entry => entry.installmentId === installment.id && !entry.voidedAt)" data-validation-anchor="payment-history" data-validation-focus="payment-history" tabindex="-1" class="sm:col-span-2 rounded-lg border border-emerald-100 bg-emerald-50/50 p-3 text-xs">
                    <summary class="cursor-pointer font-semibold text-emerald-900">ประวัติรับเงินจริง ({{ paymentHistoryRows.filter(entry => entry.installmentId === installment.id && !entry.voidedAt).length }})</summary>
                    <div class="mt-2 space-y-2">
                    <div v-for="entry in paymentHistoryRows.filter(item => item.installmentId === installment.id && !item.voidedAt)" :key="entry.id" class="rounded-lg border border-emerald-100 bg-white p-2.5 text-xs">
                      <div class="flex flex-wrap items-center justify-between gap-2"><span>{{ formatCurrency(entry.amount) }} บาท · รับเงินจริงแล้ว</span><span>{{ formatPaymentDate(entry.paidDate) }} · {{ entry.method }}</span></div>
                      <p v-if="entry.reference || entry.note" class="mt-1 text-slate-500">{{ [entry.reference, entry.note].filter(Boolean).join(' · ') }}</p>
                      <div v-if="!entry.voidedAt" class="mt-2 flex flex-wrap gap-2">
                        <a v-if="entry.slipUrl" :href="entry.slipUrl" target="_blank" rel="noopener" class="rounded border border-slate-200 px-2 py-1">เปิดสลิป</a>
                        <button v-if="!entry.hasReceipt" type="button" @click="voidPaymentEntry(entry.id)" :disabled="saving" class="rounded border border-red-200 px-2 py-1 text-red-600">ยกเลิกรายการที่บันทึกผิด</button>
                      </div>
                    </div>
                    </div>
                  </details>
                  <div class="sm:col-span-2 flex justify-end"><button type="button" @click="removeDepositInstallment(index)" :disabled="saving" class="h-9 w-9 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50 flex items-center justify-center" title="ลบงวดนี้"><i class="ph ph-trash"></i></button></div>
                </div>
                <button type="button" @click="addDepositInstallment" class="inline-flex min-h-10 items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium bg-white border border-amber-300 text-amber-800 hover:bg-amber-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2 active:translate-y-px"><i class="ph ph-plus"></i> เพิ่มมัดจำ</button>
                <p class="text-xs text-slate-500">ยอดคงเหลือคำนวณให้อัตโนมัติจากยอดงาน หักด้วยเงินจริงที่ได้รับแล้ว ไม่ต้องเพิ่มงวดสุดท้าย</p>
              </div>

              <div v-if="canReceiveFinalBalance" class="rounded-xl border border-blue-200 bg-blue-50/60 p-3 space-y-3">
                <div class="flex flex-wrap items-start justify-between gap-3">
                  <div>
                    <strong class="text-sm text-blue-900">ยอดคงเหลือ · คำนวณอัตโนมัติ</strong>
                    <p class="mt-1 text-xs text-slate-600">ยอดที่เหลือหลังรับมัดจำครบแล้ว ไม่ต้องเพิ่มรอบเอง</p>
                    <p class="mt-1 text-lg font-bold text-blue-800">{{ formatCurrency(depositBalanceTotal) }} บาท</p>
                    <p class="text-xs text-emerald-800">ยอดที่รับสำหรับส่วนนี้ {{ formatCurrency(finalBalanceReceivedTotal) }} บาท<span v-if="finalBalanceReceivedDate"> · ล่าสุด {{ formatPaymentDate(finalBalanceReceivedDate) }}</span></p>
                  </div>
                  <button type="button" @click="openPaymentEntry('$balance')" :disabled="saving || legacyPaymentReviewRequired" class="inline-flex min-h-10 items-center gap-1.5 rounded-lg border border-blue-300 bg-white px-3 py-2 text-xs font-semibold text-blue-900 hover:bg-blue-100 disabled:opacity-50"><i class="ph ph-hand-coins"></i> ใส่ยอดที่ได้รับจริง</button>
                </div>
                <label class="block max-w-sm text-xs text-slate-600">กำหนดชำระยอดคงเหลือวันที่<input type="date" :value="form['Payment Due Date'] || autoPaymentDuePreview" @change="form['Payment Due Date'] = ($event.target as HTMLInputElement).value; paymentDueMode = 'manual'; syncPaymentDueModeToForm()" data-validation-focus="balance-due-date" class="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm" /></label>
                <div v-if="finalBalancePayments.length" class="flex flex-wrap gap-2">
                  <template v-for="entry in finalBalancePayments" :key="`balance-receipt-${entry.id}`">
                    <a v-if="entry.receiptUrl" :href="entry.receiptUrl" target="_blank" rel="noopener" class="inline-flex min-h-9 items-center rounded-lg border border-emerald-300 bg-white px-3 py-2 text-xs font-semibold text-emerald-900">เปิดใบเสร็จ · ยอดคงเหลือ</a>
                  </template>
                </div>
                <div v-if="paymentEntryDraft.open && paymentEntryDraft.installmentId === '$balance'" data-validation-anchor="payment-entry" tabindex="-1" class="rounded-lg border border-blue-200 bg-white p-3 space-y-3">
                  <div><strong class="text-sm text-blue-900">บันทึกยอดที่ได้รับจริง · ยอดคงเหลือ</strong><p class="text-xs text-slate-600">ยอดคงเหลือรวมส่วนเพิ่มตามแผน {{ formatCurrency(depositBalanceTotal) }} บาท · กำหนดชำระ {{ formatPaymentDate(form['Payment Due Date'] || autoPaymentDuePreview) }}</p><p class="mt-1 text-xs text-amber-700">กรอกยอดเงินจริงที่ได้รับ; ปุ่มเติมยอดด้านล่างเป็นตัวช่วยกรอกเท่านั้น</p></div>
                  <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label class="text-xs text-slate-600">ยอดที่ได้รับจริง (บาท)<input v-model="paymentEntryDraft.amount" type="number" min="0.01" step="0.01" class="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm" /></label>
                      <button type="button" @click="paymentEntryDraft.amount = paymentEntrySuggestedAmount" class="mt-1 text-xs font-medium text-blue-700 underline underline-offset-2">เติมยอดคงเหลือ {{ formatCurrency(paymentEntrySuggestedAmount) }} บาท</button>
                    </div>
                    <label class="text-xs text-slate-600">วันที่ได้รับเงินจริง<input v-model="paymentEntryDraft.paidDate" type="date" class="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm" /></label>
                    <div class="sm:col-span-2 text-xs text-slate-600">
                      <span class="block mb-1">สลิป (ถ้ามี)</span>
                      <label for="payment-slip-file-input-balance" class="relative inline-flex min-h-10 items-center gap-2 overflow-hidden rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 cursor-pointer hover:border-primary hover:bg-blue-50 focus-within:ring-2 focus-within:ring-primary/30" :class="isUploadingPaymentSlip ? 'pointer-events-none opacity-60' : ''">
                        <i :class="isUploadingPaymentSlip ? 'ph ph-spinner animate-spin' : 'ph ph-upload-simple'"></i>
                        {{ isUploadingPaymentSlip ? 'กำลังอัปโหลด...' : paymentSlipFileName ? 'เปลี่ยนไฟล์สลิป' : 'อัปโหลดสลิป' }}
                        <input id="payment-slip-file-input-balance" type="file" :accept="SLIP_FILE_ACCEPT" :disabled="isUploadingPaymentSlip || saving" aria-label="อัปโหลดสลิป" class="absolute inset-0 h-full w-full cursor-pointer opacity-0" @change="handlePaymentSlipUpload" />
                      </label>
                      <span class="ml-2 text-slate-400">{{ SLIP_SUPPORTED_FORMATS_LABEL }} · {{ SLIP_MAX_FILE_SIZE_LABEL }}</span>
                      <div v-if="paymentEntryDraft.slipUrl" class="mt-2 flex flex-wrap items-center gap-2 rounded-lg bg-emerald-50 px-3 py-2 text-emerald-800"><i class="ph ph-check-circle"></i><span class="min-w-0 flex-1 truncate">{{ paymentSlipFileName || 'อัปโหลดหลักฐานแล้ว' }}</span><a :href="paymentEntryDraft.slipUrl" target="_blank" rel="noopener" class="shrink-0 underline underline-offset-2">เปิดไฟล์ที่อัปโหลด</a></div>
                    </div>
                  </div>
                  <p v-if="paymentEntryError" role="alert" class="text-sm text-red-600">{{ paymentEntryError }}</p>
                  <div class="flex flex-wrap gap-2"><button type="button" data-validation-focus="payment-entry" @click="recordPaymentEntry" :disabled="isUploadingPaymentSlip" class="rounded-lg bg-emerald-600 px-3 py-2 text-sm text-white disabled:opacity-50">บันทึกยอดที่ได้รับจริง</button><button type="button" @click="paymentEntryDraft.open = false" :disabled="isUploadingPaymentSlip" class="rounded-lg border border-slate-200 px-3 py-2 text-sm disabled:opacity-50">ยกเลิก</button></div>
                </div>
              </div>

              <p v-if="otherUnassignedPaymentTotal > 0" class="text-xs text-amber-800">มียอดรับเงินจริงเดิมที่ยังไม่ได้ผูกกับมัดจำ {{ formatCurrency(otherUnassignedPaymentTotal) }} บาท ระบบรวมยอดนี้ในการคำนวณแล้ว</p>
              <p v-if="paymentSummary.overpaid > 0" class="text-sm text-red-700">รับเงินจริงเกินยอดเต็มงาน {{ formatCurrency(paymentSummary.overpaid) }} บาท</p>

              <details v-if="paymentHistoryRows.some(entry => !entry.installmentId && entry.note !== 'ยอดคงเหลือ' && !entry.voidedAt)" data-validation-anchor="payment-history" data-validation-focus="payment-history" tabindex="-1" class="rounded-lg border border-slate-200 bg-white p-3">
                <summary class="cursor-pointer text-sm font-semibold text-slate-700">ประวัติรับเงินเดิมที่ยังไม่ได้ผูกกับมัดจำ</summary>
                <div class="mt-2 space-y-2">
                <div v-for="entry in paymentHistoryRows.filter(item => !item.installmentId && item.note !== 'ยอดคงเหลือ' && !item.voidedAt)" :key="entry.id" class="rounded-lg border border-slate-200 p-3 text-xs">
                  <p>{{ formatCurrency(entry.amount) }} บาท · {{ formatPaymentDate(entry.paidDate) }} · {{ entry.method }}</p>
                  <p v-if="entry.note" class="mt-1 text-slate-500">{{ entry.note }}</p>
                  <div v-if="!entry.voidedAt" class="mt-2 flex flex-wrap gap-2"><a v-if="entry.receiptUrl" :href="entry.receiptUrl" target="_blank" rel="noopener" class="rounded border border-emerald-200 px-2 py-1 text-xs text-emerald-700">เปิดใบเสร็จครั้งนี้</a><a v-if="entry.slipUrl" :href="entry.slipUrl" target="_blank" rel="noopener" class="rounded border border-slate-200 px-2 py-1 text-xs">เปิดสลิป</a><button v-if="!entry.hasReceipt" type="button" @click="voidPaymentEntry(entry.id)" :disabled="saving" class="rounded border border-red-200 px-2 py-1 text-xs text-red-600">ยกเลิกรายการที่บันทึกผิด</button></div>
                </div>
                </div>
              </details>
              <details v-if="voidedPaymentHistoryRows.length" class="rounded-lg border border-slate-200 bg-white p-3 text-xs text-slate-600">
                <summary class="cursor-pointer font-medium">ดูประวัติรายการที่ยกเลิก ({{ voidedPaymentHistoryRows.length }}) · ไม่นับรวมยอดรับเงินจริง</summary>
                <div class="mt-3 space-y-2">
                  <div v-for="entry in voidedPaymentHistoryRows" :key="entry.id" class="rounded border border-slate-200 bg-slate-50 p-2.5">
                    <div class="flex flex-wrap items-center justify-between gap-2"><span>{{ entry.roundLabel }} · {{ formatCurrency(entry.amount) }} บาท · ยกเลิกรายการ</span><span>{{ formatPaymentDate(entry.paidDate) }} · {{ entry.method }}</span></div>
                    <p v-if="entry.voidReason" class="mt-1 text-slate-500">เหตุผล: {{ entry.voidReason }}</p>
                    <p v-if="entry.reference || entry.note" class="mt-1 text-slate-500">{{ [entry.reference, entry.note].filter(Boolean).join(' · ') }}</p>
                  </div>
                </div>
              </details>
              <details class="rounded-lg border border-slate-200 bg-white p-3">
                <summary class="cursor-pointer text-sm font-medium text-slate-700">ข้อมูลเพิ่มเติม · เงื่อนไขการชำระ</summary>
                <label class="mt-3 block text-sm text-slate-700">เงื่อนไขการชำระ / ยกเลิก / เลื่อนงาน<textarea v-model="form['Payment Terms']" rows="3" placeholder="ระบุข้อตกลงที่ตกลงกับลูกค้า เช่น วันชำระ การเลื่อนงานและเงื่อนไขคืนเงิน" class="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm"></textarea></label>
              </details>
            </div>
            <div v-else-if="depositFeatureAvailable" class="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3">
              <div><strong class="block text-sm text-slate-700">ระบบมัดจำปิดอยู่ · ใช้แบบเดิม</strong><p class="text-xs text-slate-500 mt-0.5">เปิดเมื่อต้องการระบุยอดมัดจำและวันรับเงินแยกเป็นรอบ</p></div>
              <button type="button" :aria-pressed="false" @click="form['Deposit Enabled'] = true" class="rounded-lg border border-amber-300 bg-white px-3 py-2 text-sm text-amber-800">เปิดระบบมัดจำ</button>
            </div>
            <div v-else class="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
              ระบบแบ่งงวดถูกปิดจาก <strong>ตั้งค่า &gt; ใบเสนอราคา</strong> — เปิดระบบส่วนกลางที่นั่นก่อน หากต้องการกำหนดมัดจำและยอดที่แสดงในใบเสนอราคา
            </div>
          </div>

          <!-- Status & Payment -->
          <div class="md:col-span-2 grid grid-cols-1 md:grid-cols-2 gap-6 bg-slate-50 p-4 rounded-xl border border-slate-100">
            <div>
              <label class="block text-sm font-medium text-slate-700 mb-1">สถานะ</label>
              <select
                v-model="form.Status"
                class="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
              >
                <option value="รอชำระ">รอชำระ</option>
                <option value="เสร็จสิ้น">เสร็จสิ้น</option>
                <option value="ยกเลิก">ยกเลิก</option>
              </select>
            </div>
            <div v-if="!depositSystemEnabled" data-validation-anchor="legacy-payment" tabindex="-1" class="md:col-span-2 rounded-lg border border-slate-200 bg-white p-3">
              <p class="mb-3 text-sm font-medium text-slate-700">บันทึกยอดที่ได้รับเงินจริง</p>
              <div class="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <label class="block text-sm text-slate-700">
                  ยอดที่ได้รับจริง (บาท)
                  <input
                    v-model="form['Actual Received']"
                    data-validation-focus="actual-received"
                    type="number"
                    min="0"
                    step="0.01"
                    placeholder="กรอกเมื่อได้รับเงินจริง"
                    class="mt-1 w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </label>
                <label class="block text-sm text-slate-700">
                  วันที่ได้รับเงินจริง
                  <input
                    v-model="form['Payment Date']"
                    data-validation-focus="received-date"
                    type="date"
                    class="mt-1 w-full rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  />
                </label>
              </div>
              <p class="mt-2 text-xs text-slate-500">เมื่อได้รับเงิน ให้กรอกยอดเงินจริงและวันที่รับเงินทุกครั้ง · หากยังไม่ได้รับเงิน เว้นว่างไว้ได้</p>
            </div>
            <div v-if="!depositSystemEnabled" class="md:col-span-2">
              <label class="block text-sm font-medium text-slate-700 mb-2">โหมดกำหนดวันชำระ</label>
              <div class="flex flex-wrap gap-4 text-sm text-slate-700">
                <label class="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    class="text-primary focus:ring-primary"
                    value="auto"
                    :disabled="!!finalInstallment"
                    v-model="paymentDueMode"
                    @change="syncPaymentDueModeToForm"
                  />
                  อัตโนมัติ (วันงาน + 1 เดือน)
                </label>
                <label class="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    class="text-primary focus:ring-primary"
                    value="manual"
                    v-model="paymentDueMode"
                    @change="syncPaymentDueModeToForm"
                  />
                  กำหนดเอง
                </label>
                <label class="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="radio"
                    class="text-primary focus:ring-primary"
                    value="none"
                    v-model="paymentDueMode"
                    @change="syncPaymentDueModeToForm"
                  />
                  ไม่ต้องแจ้งเตือน
                </label>
              </div>
            </div>
            <div v-if="!depositSystemEnabled">
              <label class="block text-sm font-medium text-slate-700 mb-1">กำหนดชำระ</label>
              <input
                type="date"
                v-model="form['Payment Due Date']"
                :readonly="paymentDueMode === 'auto' || !!finalInstallment"
                :disabled="paymentDueMode === 'none'"
                :class="[
                  'w-full border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary',
                  paymentDueMode === 'auto' || paymentDueMode === 'none' || finalInstallment ? 'bg-slate-100 text-slate-600' : 'bg-white',
                ]"
              />
              <p v-if="finalInstallment && paymentDueMode !== 'none'" class="text-xs text-slate-500 mt-1">
                ใช้วันชำระจากงวดยอดสุดท้ายด้านบน แก้วันที่ในงวดนั้นเพื่อให้ตรงกันในเอกสาร
              </p>
              <p v-else-if="paymentDueMode === 'auto'" class="text-xs text-slate-500 mt-1">
                คำนวณอัตโนมัติ{{
                  autoPaymentDuePreview ? ': ' + formatPaymentDate(autoPaymentDuePreview) : ' (กรอกวันงานก่อน)'
                }}
              </p>
              <p v-else-if="paymentDueMode === 'none'" class="text-xs text-slate-500 mt-1">
                จะไม่มีการแจ้งเตือนหรือแสดงสถานะเกินกำหนดชำระสำหรับงานนี้
              </p>
            </div>
            <div v-if="!depositSystemEnabled" class="md:col-span-2">
              <label class="inline-flex items-center gap-2 text-sm text-slate-700">
                <input
                  type="checkbox"
                  v-model="form['Show Payment Due on Quotation']"
                  class="rounded border-slate-300 text-primary focus:ring-primary"
                />
                แสดงกำหนดชำระในใบเสนอราคา / Invoice / Receipt
              </label>
            </div>
            <div v-if="!depositSystemEnabled" class="md:col-span-2">
              <label class="block text-sm font-medium text-slate-700 mb-1">Slip Reference (Upload File)</label>
              <div class="flex flex-col gap-2 max-w-2xl">
                <input
                  id="job-slip-file-input"
                  type="file"
                  :accept="SLIP_FILE_ACCEPT"
                  multiple
                  class="hidden"
                  @change="handleSlipUpload"
                />
                <div
                  class="relative w-full rounded-lg border-2 border-dashed transition-colors"
                  :class="
                    slipDragActive
                      ? 'border-primary bg-primary/5'
                      : hasSlipAttachment
                        ? 'border-slate-200 bg-white'
                        : 'border-slate-300 bg-slate-50'
                  "
                  @dragover="onSlipDragOver"
                  @dragleave="onSlipDragLeave"
                  @drop="onSlipDrop"
                >
                  <div
                    v-if="!hasSlipAttachment"
                    role="button"
                    tabindex="0"
                    class="px-4 py-6 text-center text-sm text-slate-600 cursor-pointer hover:bg-white/80"
                    @click="openSlipFilePicker"
                    @keydown.enter.prevent="openSlipFilePicker"
                    @keydown.space.prevent="openSlipFilePicker"
                  >
                    <i class="ph ph-upload-simple text-2xl mb-2 block text-slate-400"></i>
                    <div class="font-medium">คลิกเพื่อเลือกไฟล์ หรือลากวางที่นี่</div>
                    <div class="text-xs text-slate-500 mt-1">รองรับ {{ SLIP_SUPPORTED_FORMATS_LABEL }} · {{ SLIP_MAX_FILE_SIZE_LABEL }} · สูงสุด 10 ไฟล์</div>
                  </div>

                  <div v-else class="p-3">
                    <div class="grid grid-cols-2 sm:grid-cols-3 gap-3">
                      <div
                        v-for="(item, index) in slipItems"
                        :key="item.fileUrl + index"
                        class="relative rounded-lg border border-slate-200 bg-slate-50 overflow-hidden"
                      >
                        <button
                          v-if="!isUploadingSlip && !isRemovingSlip"
                          type="button"
                          class="absolute top-1.5 right-1.5 z-10 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-white hover:bg-red-600 transition-colors"
                          :title="`ลบ ${item.displayName}`"
                          :aria-label="`ลบ ${item.displayName}`"
                          @click.stop="removeSlipAt(index)"
                        >
                          <i class="ph ph-x text-xs"></i>
                        </button>
                        <a
                          :href="item.fileUrl"
                          target="_blank"
                          rel="noopener noreferrer"
                          class="block"
                          @click.stop
                        >
                          <img
                            v-if="item.previewUrl"
                            :src="item.previewUrl"
                            referrerpolicy="no-referrer"
                            class="w-full h-28 object-contain bg-white"
                            :alt="item.displayName"
                          />
                          <div
                            v-else
                            class="flex h-28 flex-col items-center justify-center px-2 text-center text-xs text-slate-600"
                          >
                            <i
                              class="text-3xl mb-1"
                              :class="item.isPdf ? 'ph ph-file-pdf text-red-500' : 'ph ph-file-image text-slate-400'"
                            ></i>
                            <span class="line-clamp-2">{{ item.displayName }}</span>
                          </div>
                          <div
                            v-if="item.isPdf && item.previewUrl"
                            class="absolute bottom-7 left-1.5 rounded bg-black/60 px-1.5 py-0.5 text-[10px] text-white"
                          >
                            PDF
                          </div>
                        </a>
                        <div class="truncate border-t border-slate-200 bg-white px-2 py-1 text-[11px] text-slate-500">
                          {{ item.displayName }}
                        </div>
                      </div>

                      <div
                        v-if="canAddMoreSlips"
                        role="button"
                        tabindex="0"
                        class="flex min-h-32 flex-col items-center justify-center rounded-lg border border-dashed border-slate-300 bg-slate-50 px-3 py-4 text-center text-xs text-slate-500 cursor-pointer hover:border-primary/60 hover:bg-primary/5 hover:text-primary"
                        @click="openSlipFilePicker"
                        @keydown.enter.prevent="openSlipFilePicker"
                        @keydown.space.prevent="openSlipFilePicker"
                      >
                        <i class="ph ph-plus text-xl mb-1"></i>
                        <span>เพิ่มไฟล์</span>
                      </div>
                    </div>
                    <div class="mt-2 text-center text-xs text-slate-500">
                      {{ slipItems.length }}/10 ไฟล์ · ลากวางเพิ่มได้
                    </div>
                  </div>

                  <div
                    v-if="isUploadingSlip || isRemovingSlip"
                    class="absolute inset-0 flex items-center justify-center bg-white/85 text-sm font-medium text-primary"
                  >
                    {{ isRemovingSlip ? 'กำลังลบสลิป...' : slipUploadProgress || 'กำลังอัปโหลดสลิป...' }}
                  </div>
                </div>
                <div
                  v-if="slipUploadError"
                  role="alert"
                  class="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-700"
                >
                  {{ slipUploadError }}
                </div>
              </div>
            </div>
          </div>

          <!-- Location -->
          <div class="md:col-span-2">
            <div class="mb-1 flex items-center justify-between gap-2">
              <label class="block text-sm font-medium text-slate-700">สถานที่</label>
              <button
                type="button"
                title="เพิ่มสถานที่/แผนที่ที่ใช้ซ้ำ"
                aria-label="เพิ่มสถานที่โปรด"
                class="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-blue-200 bg-blue-50 text-blue-700 hover:bg-blue-100"
                @click="openLocationMapFavoriteEditor(false)"
              ><i class="ph ph-plus"></i></button>
            </div>
            <input
              v-model="form.Location"
              placeholder="Building, Room, City"
              class="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
            />
            <div v-if="locationMapFavorites.length" class="mt-2 flex flex-wrap gap-2">
              <div
                v-for="item in locationMapFavorites"
                :key="item.id"
                class="inline-flex max-w-full items-stretch overflow-hidden rounded-lg border border-slate-200 bg-slate-50"
              >
                <button
                  type="button"
                  :disabled="savingLocationMapFavorite"
                  :title="item.favorite ? 'นำออกจากรายการโปรด' : 'ปักหมุดเป็นสถานที่โปรด'"
                  :aria-label="`${item.favorite ? 'นำออกจาก' : 'เพิ่มใน'}รายการโปรด: ${item.name}`"
                  :aria-pressed="item.favorite"
                  class="shrink-0 border-r border-slate-200 px-2 text-amber-500 hover:bg-amber-50 disabled:opacity-50"
                  @click="toggleLocationMapFavorite(item.id)"
                ><i :class="item.favorite ? 'ph-fill ph-star' : 'ph ph-star'" /></button>
                <button
                  type="button"
                  class="min-w-0 px-2.5 py-1.5 text-left hover:bg-white"
                  :title="item.location || item.mapUrl || item.name"
                  @click="applyLocationMapFavorite(item)"
                >
                  <span class="block max-w-[200px] truncate text-xs font-semibold text-slate-700">{{ item.name }}</span>
                  <span v-if="item.location" class="block max-w-[200px] truncate text-[10px] text-slate-500">{{ item.location }}</span>
                </button>
                <a
                  v-if="item.mapUrl"
                  :href="item.mapUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                  title="เปิดแผนที่ในแท็บใหม่"
                  class="inline-flex shrink-0 items-center px-2 text-blue-600 hover:bg-blue-50"
                  @click.stop
                ><i class="ph ph-arrow-square-out" /></a>
              </div>
            </div>
          </div>

          <!-- Google Maps -->
          <div class="md:col-span-2">
            <div class="mb-1 flex items-center justify-between gap-2">
              <label class="block text-sm font-medium text-slate-700">Google Maps Link</label>
              <button
                type="button"
                :disabled="savingLocationMapFavorite"
                :aria-pressed="currentMapIsFavorite"
                :title="currentMapIsFavorite ? 'นำลิงก์นี้ออกจากรายการโปรด' : 'ปักหมุดลิงก์แผนที่ที่ใช้ประจำ'"
                :aria-label="currentMapIsFavorite ? 'นำลิงก์แผนที่ออกจากรายการโปรด' : 'เพิ่มลิงก์แผนที่ในรายการโปรด'"
                class="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-amber-200 bg-amber-50 text-amber-500 hover:bg-amber-100 disabled:opacity-50"
                @click="openLocationMapFavoriteEditor(true)"
              ><i :class="currentMapIsFavorite ? 'ph-fill ph-star' : 'ph ph-star'" /></button>
            </div>
            <input
              type="url"
              v-model="form['Google Maps Link']"
              placeholder="https://maps.google.com/..."
              class="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary text-blue-600"
            />
            <p v-if="locationMapFavoriteError && !showLocationMapFavoriteEditor" role="alert" class="mt-1 text-xs text-red-600">{{ locationMapFavoriteError }}</p>

            <div v-if="showLocationMapFavoriteEditor" class="mt-2 rounded-xl border border-blue-200 bg-blue-50/70 p-3 space-y-2">
              <div class="text-sm font-semibold text-slate-800">เพิ่มสถานที่และลิงก์แผนที่</div>
              <label class="block text-xs font-medium text-slate-700">
                ชื่อสถานที่ / ชื่อแผนที่ <span class="text-red-500">*</span>
                <input
                  v-model="locationMapFavoriteDraft.name"
                  type="text"
                  maxlength="100"
                  placeholder="เช่น ไบเทค บางนา · Hall 98"
                  class="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-normal focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
                  @keydown.enter.prevent="saveLocationMapFavorite"
                />
              </label>
              <label class="inline-flex cursor-pointer items-center gap-2 text-xs text-slate-700">
                <input v-model="locationMapFavoriteDraft.favorite" type="checkbox" class="rounded border-slate-300 text-amber-500 focus:ring-amber-400" />
                <i class="ph-fill ph-star text-amber-500" /> ปักหมุดเป็นสถานที่/แผนที่โปรด
              </label>
              <p class="text-[11px] text-slate-500">บันทึกสถานที่และลิงก์ที่กรอกไว้ด้านบน เพื่อเลือกใช้กับงานครั้งต่อไป</p>
              <p v-if="locationMapFavoriteError" role="alert" class="text-xs text-red-700">{{ locationMapFavoriteError }}</p>
              <div class="flex justify-end gap-2">
                <button type="button" class="rounded-lg border border-slate-300 bg-white px-3 py-1.5 text-xs text-slate-700 hover:bg-slate-50 disabled:opacity-50" :disabled="savingLocationMapFavorite" @click="cancelLocationMapFavoriteEditor">ยกเลิก</button>
                <button type="button" class="inline-flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-50" :disabled="savingLocationMapFavorite" @click="saveLocationMapFavorite">
                  <i :class="savingLocationMapFavorite ? 'ph ph-spinner animate-spin' : 'ph ph-plus'" />
                  {{ savingLocationMapFavorite ? 'กำลังบันทึก...' : 'เพิ่มสถานที่' }}
                </button>
              </div>
            </div>
          </div>

          <!-- Notes -->
          <div class="md:col-span-2">
            <label class="block text-sm font-medium text-slate-700 mb-1">หมายเหตุ</label>
            <textarea
              v-model="form.Notes"
              rows="3"
              placeholder="Additional details..."
              class="w-full max-h-56 overflow-y-auto bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary custom-scrollbar job-content-text"
            ></textarea>
          </div>

          <!-- Document Language -->
          <div class="md:col-span-2 p-3 bg-slate-50 border border-slate-200 rounded-lg">
            <div class="text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5 flex items-center gap-1">
              <i class="ph ph-translate"></i> ภาษาเอกสาร PDF
              <span class="text-[10px] text-slate-400 font-normal normal-case tracking-normal ml-1"
                >(ใบเสนอราคา · Invoice · ใบเสร็จ)</span
              >
            </div>
            <div class="flex gap-1.5">
              <button
                type="button"
                @click="form['Document Language'] = 'th'"
                :class="[
                  'flex-1 px-3 py-2 text-sm font-medium rounded-md border transition-colors',
                  (form['Document Language'] || 'th') === 'th'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100',
                ]"
              >
                🇹🇭 ไทย
              </button>
              <button
                type="button"
                @click="form['Document Language'] = 'en'"
                :class="[
                  'flex-1 px-3 py-2 text-sm font-medium rounded-md border transition-colors',
                  (form['Document Language'] || 'th') === 'en'
                    ? 'bg-blue-600 text-white border-blue-600'
                    : 'bg-white text-slate-600 border-slate-200 hover:bg-slate-100',
                ]"
              >
                🇬🇧 English
              </button>
            </div>
          </div>

          <!-- Deposit mode keeps selection, preview, generation, and file links together. -->
          <div class="md:col-span-2 doc-card">
            <div class="doc-head">
              <span class="doc-overline">{{ depositSystemEnabled ? 'เอกสารมัดจำ' : 'เอกสาร' }}</span>
              <span class="doc-head-sub">ใบเสนอราคา · Invoice · ใบเสร็จ</span>
            </div>

            <div v-if="depositSystemEnabled" class="rounded-xl border border-blue-200 bg-blue-50/50 p-3 sm:p-4 space-y-4">
              <div class="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <label class="block text-sm font-medium text-slate-700">วันที่ออกใบเสนอราคา
                  <input type="date" v-model="form['Quotation Date']" class="mt-1 w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary" />
                  <span class="block text-xs font-normal text-slate-400 mt-1">ว่างไว้ = ใช้วันที่กดสร้างไฟล์</span>
                </label>

                <div v-if="quotationPaymentChoices.length" data-validation-anchor="quotation-payment-selection" tabindex="-1" class="min-w-0">
                  <label for="deposit-quotation-payment-selection" class="block text-sm font-semibold text-slate-800 mb-1">ยอดที่ต้องการให้ระบุในใบเสนอราคา <span class="text-red-600">*</span></label>
                  <div class="relative">
                    <select id="deposit-quotation-payment-selection" v-model="quotationPaymentSelectionId" data-validation-focus="quotation-payment-selection" aria-required="true" :aria-invalid="error.includes('ใบเสนอราคา') && !quotationPaymentSelectionId" :disabled="saving" :class="['quote-payment-select', error.includes('ใบเสนอราคา') && !quotationPaymentSelectionId ? 'quote-payment-select-error' : quotationPaymentSelectionId ? 'quote-payment-select-success' : '']">
                      <option value="">เลือกยอดก่อน Preview หรือสร้าง PDF</option>
                      <option v-for="choice in quotationPaymentChoices" :key="choice.id" :value="choice.id">
                        {{ choice.label }} · {{ formatCurrency(choice.amount) }} บาท{{ choice.dueDate ? ' · ชำระวันที่ ' + formatPaymentDate(choice.dueDate) : '' }}{{ choice.paid ? ' · ชำระครบแล้ว' : '' }}
                      </option>
                    </select>
                    <i :class="saving ? 'ph ph-spinner animate-spin' : 'ph ph-caret-down'" class="quote-payment-select-icon" aria-hidden="true"></i>
                  </div>
                  <p v-if="error.includes('ใบเสนอราคา') && !quotationPaymentSelectionId" class="mt-1 text-xs font-medium text-red-600">ยังไม่ได้เลือกยอด — เลือกยอดเต็มคงเหลือหรือมัดจำ/งวดที่ต้องการก่อนทำต่อ</p>
                </div>
                <div v-else data-validation-anchor="quotation-payment-selection" tabindex="-1" class="rounded-lg border border-amber-300 bg-white p-3 text-sm text-slate-700 sm:col-span-2">
                  <strong class="block text-slate-800">ยังไม่มีแผนงวดให้เลือกในใบเสนอราคา</strong>
                  <p class="mt-1 text-xs text-slate-600">ไปที่ส่วน “แบ่งงวดชำระและรับเงินจริง” ด้านบน เพิ่มงวดและกำหนดยอด/วันที่ แล้วกลับมาเลือกยอดที่ต้องการ</p>
                </div>
              </div>

              <p class="text-xs text-slate-600">ใบเสนอราคายังคงแสดงยอดเต็มที่ตกลง รับเงินจริงสะสม และยอดคงเหลือแยกกัน ตัวเลือกนี้ระบุยอด/งวดที่ให้อ้างอิงในเอกสารเท่านั้น</p>

              <label class="block text-sm font-medium text-slate-700">รายการรับเงินจริงสำหรับใบเสร็จ <span class="font-normal text-slate-500">(เว้นว่างเพื่อสร้างฉบับร่างยอด 0 บาท)</span>
                <select v-model="receiptPaymentId" class="mt-1 w-full rounded-lg border border-slate-300 bg-white px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30">
                  <option value="">ไม่เลือก · สร้างฉบับร่างยอด 0 บาท</option>
                  <option v-for="entry in receiptPayments" :key="entry.id" :value="entry.id">{{ formatPaymentDate(entry.paidDate) }} · {{ formatCurrency(entry.amount) }} บาท · {{ entry.reference || entry.method }}</option>
                </select>
              </label>
              <p class="text-xs text-slate-500">Preview/ใบเสนอราคาอ้างอิงยอดที่เลือก · Invoice ใช้ยอดเต็มงาน · ใบเสร็จอ้างอิงรายการรับเงินจริงที่เลือก หรือสร้างฉบับร่าง 0 บาท</p>

              <div class="border-t border-blue-200 pt-3 space-y-2">
                <p class="text-sm font-semibold text-slate-800">Preview และสร้างเอกสาร</p>
                <div class="flex flex-wrap gap-2">
                  <button type="button" @click="openFormQuotationPreview" :disabled="saving || !quotationPaymentChoices.some(choice => choice.id === quotationPaymentSelectionId)" class="inline-flex min-h-11 w-full sm:w-auto items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-blue-200 bg-white px-3 py-2 text-sm font-medium text-blue-700 disabled:cursor-not-allowed disabled:opacity-50"><i class="ph ph-eye"></i> Preview ใบเสนอราคา</button>
                  <button type="button" @click="generateDocumentsNow('quotation')" :disabled="saving || !quotationPaymentChoices.some(choice => choice.id === quotationPaymentSelectionId)" class="inline-flex min-h-11 w-full sm:w-auto items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-blue-200 bg-blue-600 px-3 py-2 text-sm font-medium text-white disabled:cursor-not-allowed disabled:opacity-50"><i class="ph ph-file-pdf"></i> สร้างใบเสนอราคา PDF</button>
                  <button type="button" @click="generateDocumentsNow('invoice')" :disabled="saving || !quotationPaymentChoices.some(choice => choice.id === quotationPaymentSelectionId)" class="inline-flex min-h-11 w-full sm:w-auto items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-violet-200 bg-violet-50 px-3 py-2 text-sm font-medium text-violet-700 disabled:cursor-not-allowed disabled:opacity-50"><i class="ph ph-file-text"></i> สร้าง Invoice PDF</button>
                  <button type="button" @click="generateDocumentsNow('receipt', receiptPaymentId || undefined, !receiptPaymentId && quotationPaymentSelectionId !== '$full' ? quotationPaymentSelectionId : undefined)" :disabled="saving" class="inline-flex min-h-11 w-full sm:w-auto items-center justify-center gap-1.5 whitespace-nowrap rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2 text-sm font-medium text-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"><i class="ph ph-receipt"></i> {{ receiptPaymentId || (!receiptPayments.length && depositPaidTotal > 0) ? 'สร้างใบเสร็จ PDF' : 'สร้างใบเสร็จฉบับร่าง PDF' }}</button>
                  <button type="button" @click="generateDocumentsNow('quotation-invoice')" :disabled="saving || !quotationPaymentChoices.some(choice => choice.id === quotationPaymentSelectionId)" class="inline-flex min-h-11 w-full sm:w-auto items-center justify-center whitespace-nowrap rounded-lg border border-slate-300 bg-white px-3 py-2 text-sm font-medium text-slate-700 disabled:cursor-not-allowed disabled:opacity-50">สร้างใบเสนอราคา + Invoice</button>
                </div>
                <p class="doc-cta-hint">ปุ่มสร้าง PDF จะบันทึกงานก่อนสร้างเอกสาร ไฟล์เดิมเก็บไว้ในประวัติทุกครั้ง · หากยังไม่ได้รับเงินจริง ใบเสร็จจะเป็นฉบับร่างยอด 0 บาท</p>
              </div>
            </div>

            <div v-else class="space-y-4">
              <div>
                <label class="block text-sm font-medium text-slate-700 mb-1">วันที่ออกใบเสนอราคา</label>
                <input type="date" v-model="form['Quotation Date']" class="w-full bg-white border border-slate-300 rounded-lg px-4 py-2.5 text-sm focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary" />
                <p class="text-xs text-slate-400 mt-1">ว่างไว้ = ใช้วันที่กดสร้างไฟล์</p>
              </div>
              <div v-if="receiptPayments.length"><label class="block text-sm text-slate-700">รายการรับเงินจริงสำหรับใบเสร็จ (เว้นว่างเพื่อสร้างฉบับร่างยอด 0 บาท)<select v-model="receiptPaymentId" class="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm"><option value="">ไม่เลือก · สร้างฉบับร่าง ยังไม่ได้รับเงินจริง</option><option v-for="entry in receiptPayments" :key="entry.id" :value="entry.id">{{ formatPaymentDate(entry.paidDate) }} · {{ formatCurrency(entry.amount) }} บาท · {{ entry.reference || entry.method }}</option></select></label></div>
              <div class="flex flex-wrap gap-2">
                <button type="button" @click="generateDocumentsNow('quotation')" :disabled="saving" class="rounded-lg border border-blue-200 bg-blue-50 px-3 py-2.5 text-sm text-blue-700 disabled:opacity-50"><i class="ph ph-file-pdf"></i> สร้างใบเสนอราคา PDF</button>
                <button type="button" @click="generateDocumentsNow('invoice')" :disabled="saving" class="rounded-lg border border-violet-200 bg-violet-50 px-3 py-2.5 text-sm text-violet-700 disabled:opacity-50"><i class="ph ph-file-text"></i> สร้าง Invoice PDF</button>
                <button type="button" @click="generateDocumentsNow('receipt')" :disabled="saving" class="rounded-lg border border-emerald-200 bg-emerald-50 px-3 py-2.5 text-sm text-emerald-700 disabled:opacity-50"><i class="ph ph-receipt"></i> {{ receiptPaymentId || (!receiptPayments.length && depositPaidTotal > 0) ? 'สร้างใบเสร็จ PDF' : 'สร้างใบเสร็จฉบับร่าง PDF' }}</button>
                <button type="button" @click="generateDocumentsNow('quotation-invoice')" :disabled="saving" class="rounded-lg border border-slate-300 px-3 py-2.5 text-sm text-slate-600 disabled:opacity-50">สร้างใบเสนอราคา + Invoice</button>
              </div>
              <p class="doc-cta-hint">ปุ่มสร้าง PDF จะบันทึกงานก่อนสร้างเอกสาร ไฟล์เดิมเก็บไว้ในประวัติทุกครั้ง · หากยังไม่ได้รับเงินจริง ใบเสร็จจะเป็นฉบับร่างยอด 0 บาท</p>
            </div>

            <div v-if="generatingDocuments" class="doc-progress-wrap">
              <span class="doc-progress"></span>
              <span class="doc-progress-text">กำลังสร้างไฟล์ · อาจใช้เวลาสักครู่ (สูงสุดประมาณ 3 นาที) · กรุณาอย่าปิดหน้าต่างนี้</span>
            </div>

            <div v-if="documentLinks.length || documentsFolderUrl" class="doc-files">
              <span class="doc-overline">ไฟล์</span>
              <div class="doc-file-list">
                <div v-for="doc in documentLinks" :key="doc.label" class="doc-file-row">
                  <span class="doc-file-name">{{ doc.label }}</span>
                  <span class="doc-file-actions">
                    <a :href="doc.pdfUrl" target="_blank" rel="noopener" class="rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs font-medium text-slate-700">เปิดไฟล์</a>
                    <span class="doc-file-sep"></span>
                    <a :href="doc.downloadUrl" target="_blank" rel="noopener" class="doc-file-link is-download">
                      <i class="ph ph-download-simple"></i> ดาวน์โหลด
                    </a>
                  </span>
                </div>
              </div>
              <button v-if="documentHistoryLinks.length" type="button" @click="showDocumentHistory = !showDocumentHistory" :aria-expanded="showDocumentHistory" class="mt-2 rounded-lg border border-slate-300 bg-white px-3 py-2 text-xs text-slate-700"><i class="ph ph-clock-counter-clockwise"></i> {{ showDocumentHistory ? 'ซ่อนประวัติเอกสาร' : 'ดูประวัติเอกสาร' }} ({{ documentHistoryLinks.length }} เวอร์ชันก่อนหน้า)</button>
              <div v-if="showDocumentHistory" class="mt-3 max-h-64 overflow-y-auto space-y-2"><div v-for="doc in documentHistoryLinks" :key="doc.pdfUrl" class="rounded-lg border border-slate-200 p-3"><p class="text-sm text-slate-700">{{ doc.label }}</p><p class="text-xs text-slate-400 break-all">{{ doc.number }} {{ doc.createdAt ? '· ' + formatPaymentDate(doc.createdAt.slice(0, 10)) : '' }}</p><div class="flex gap-2 mt-2"><a :href="doc.pdfUrl" target="_blank" rel="noopener" class="rounded border border-slate-300 px-3 py-1.5 text-xs">เปิดไฟล์เวอร์ชันนี้</a><a :href="doc.downloadUrl" target="_blank" rel="noopener" class="rounded border border-slate-200 px-3 py-1.5 text-xs">ดาวน์โหลด</a></div></div></div>
              <a
                v-if="documentsFolderUrl"
                :href="documentsFolderUrl"
                target="_blank"
                rel="noopener"
                class="doc-folder-link"
              >
                เปิดโฟลเดอร์งานทั้งหมด <i class="ph ph-arrow-up-right"></i>
              </a>
            </div>
          </div>
        </form>
      </div>

      <div class="p-3 md:p-5 border-t border-slate-100 bg-slate-50 flex items-center gap-2 md:gap-3 flex-shrink-0 overflow-x-hidden">
        <button
          v-if="isEdit"
          type="button"
          @click="deleteCurrentJob"
          :disabled="deleting"
          class="flex-shrink-0 px-3 md:px-4 py-2.5 text-sm font-medium text-red-600 bg-white border border-red-200 rounded-lg hover:bg-red-50 transition-colors flex items-center disabled:opacity-70"
        >
          <i v-if="deleting" class="ph ph-spinner animate-spin sm:mr-1.5"></i>
          <i v-else class="ph ph-trash sm:mr-1.5"></i>
          <span class="hidden sm:inline">{{ deleting ? 'กำลังลบ...' : 'ลบงาน' }}</span>
        </button>

        <!-- Everything else lives in one flex-1 wrapper so it wraps and stays
             right-aligned as a single block, whether or not Delete is present. -->
        <div class="flex-1 min-w-0 flex flex-wrap items-center justify-end gap-2 md:gap-3">
          <!-- Utility actions: original distinct colors per action -->
          <button
            v-if="isEdit"
            type="button"
            @click="syncCalendarNow"
            :disabled="syncingCalendar"
            class="px-2.5 sm:px-3 md:px-4 py-2.5 text-sm font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg hover:bg-emerald-100 transition-colors flex items-center disabled:opacity-70"
          >
            <i
              class="ph sm:mr-1.5"
              :class="syncingCalendar ? 'ph-spinner animate-spin' : 'ph-calendar-check'"
            ></i>
            <span class="hidden sm:inline">{{ syncingCalendar ? 'กำลังเชื่อมต่อ...' : 'Calendar' }}</span>
          </button>
          <button
            v-if="isEdit"
            type="button"
            @click="sendLineNow"
            :disabled="sendingLine"
            class="px-2.5 sm:px-3 md:px-4 py-2.5 text-sm font-medium text-indigo-700 bg-indigo-50 border border-indigo-200 rounded-lg hover:bg-indigo-100 transition-colors flex items-center disabled:opacity-70"
          >
            <i
              class="ph sm:mr-1.5"
              :class="sendingLine ? 'ph-spinner animate-spin' : 'ph-paper-plane-tilt'"
            ></i>
            <span class="hidden sm:inline">{{ sendingLine ? 'กำลังส่ง...' : 'LINE' }}</span>
          </button>
          <button
            v-if="!depositSystemEnabled"
            type="button"
            @click="openFormQuotationPreview"
            class="px-2.5 sm:px-3 md:px-4 py-2.5 text-sm font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 transition-colors flex items-center"
          >
            <i class="ph ph-eye sm:mr-1.5"></i>
            <span class="hidden sm:inline">Preview</span>
          </button>
          <button
            v-if="!isEdit"
            type="button"
            @click="resetForm"
            class="px-3 md:px-4 py-2.5 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors flex items-center"
          >
            Clear
          </button>
          <button
            type="button"
            @click="saveDraft"
            class="px-3 md:px-4 py-2.5 text-sm font-medium text-amber-900 bg-amber-100 border border-amber-200 rounded-lg hover:bg-amber-200 transition-colors flex items-center"
          >
            <i class="ph ph-note-pencil mr-1.5"></i> บันทึกร่าง
          </button>

          <div class="hidden md:block w-px h-8 bg-slate-200"></div>

          <!-- Primary actions: cancel + submit, kept as one unbreakable unit -->
          <div class="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              @click="handleClose"
              :disabled="saving"
              class="px-3 md:px-4 py-2.5 text-sm font-medium text-slate-600 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            >
              ยกเลิก
            </button>
            <button
              type="submit"
              form="jobFormModal"
              :disabled="saving"
              class="px-4 md:px-5 py-2.5 text-sm font-medium text-white bg-primary rounded-lg hover:bg-blue-700 transition-colors flex items-center shadow-sm disabled:opacity-70"
            >
              <i v-if="saving" class="ph ph-spinner animate-spin mr-1.5"></i>
              {{
                saving
                  ? (generatingDocuments ? 'กำลังสร้างเอกสาร PDF...' : 'กำลังบันทึก...')
                  : isEdit ? 'อัปเดตงาน' : 'บันทึกงาน'
              }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>

  <!-- Quotation preview sub-modal -->
  <div v-if="isQuotationPreviewOpen" class="modal-overlay-center z-[60]">
    <div class="absolute inset-0 bg-slate-900/45 backdrop-blur-[2px]" @click="closeFormQuotationPreview"></div>
    <div
      class="confirm-modal-panel relative bg-white rounded-2xl shadow-2xl border border-slate-200 w-full max-w-4xl h-[85vh] flex flex-col overflow-hidden"
    >
      <div class="h-1.5 bg-gradient-to-r from-blue-500 via-sky-500 to-cyan-400 flex-shrink-0"></div>
      <div class="flex items-center justify-between px-5 py-4 border-b border-slate-200 flex-shrink-0">
        <div>
          <h3 class="font-bold text-slate-800">Preview ใบเสนอราคา</h3>
          <p class="text-xs text-slate-500">ตัวอย่างจากข้อมูลในฟอร์ม · กด Esc เพื่อปิด</p>
        </div>
        <button
          type="button"
          @click="closeFormQuotationPreview"
          class="w-9 h-9 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-500"
        >
          <i class="ph ph-x text-lg"></i>
        </button>
      </div>
      <div v-if="quotationPreviewHtml" class="flex-1 overflow-auto overscroll-contain bg-slate-100 custom-scrollbar p-4">
        <iframe
          :srcdoc="quotationPreviewHtml"
          class="block border-0 bg-white shadow-lg mx-auto"
          style="width: 210mm; height: 297mm; max-width: none;"
          title="Quotation Preview"
        ></iframe>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* ---- Documents card — minimal-luxury: hairlines, editorial type, one
   near-black statement colour, restrained micro-interactions. ---- */
.doc-card {
  border: 1px solid rgb(226 232 240 / 0.9);
  border-radius: 18px;
  background: #fff;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 18px;
}
.doc-head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 12px;
}
.doc-overline {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: #94a3b8;
}
.doc-head-sub {
  font-size: 11px;
  color: #cbd5e1;
  letter-spacing: 0.02em;
}

.doc-options {
  display: flex;
  flex-direction: column;
  gap: 6px;
}
.doc-option {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  text-align: left;
  font: inherit;
  color: inherit;
  background: transparent;
  padding: 12px 14px;
  border-radius: 14px;
  border: 1px solid transparent;
  cursor: pointer;
  transition: background 0.2s ease, border-color 0.2s ease;
}
.doc-option:hover {
  background: #f8fafc;
}
.doc-option.is-on {
  background: #eff6ff;
  border-color: rgb(37 99 235 / 0.15);
}
.doc-check {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  margin-top: 1px;
  border-radius: 999px;
  border: 1.5px solid #cbd5e1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  transition: background 0.2s ease, border-color 0.2s ease;
}
.doc-check i {
  font-size: 12px;
  opacity: 0;
  transform: scale(0.4);
  transition: opacity 0.18s ease, transform 0.18s ease;
}
.doc-option.is-on .doc-check {
  background: #2563eb;
  border-color: #2563eb;
}
.doc-option.is-on .doc-check i {
  opacity: 1;
  transform: scale(1);
}
.doc-option-text {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.doc-option-title {
  font-size: 14px;
  font-weight: 500;
  color: #0f172a;
}
.doc-option-sub {
  font-size: 12px;
  color: #94a3b8;
}

.doc-cta-row {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}
.doc-cta {
  appearance: none;
  border: 0;
  cursor: pointer;
  background: #2563eb;
  color: #fff;
  border-radius: 999px;
  padding: 12px 22px;
  font: inherit;
  font-size: 14px;
  font-weight: 500;
  letter-spacing: 0.01em;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}
.doc-cta:hover:not(:disabled) {
  background: #1d4ed8;
  transform: translateY(-1px);
  box-shadow: 0 12px 26px -10px rgb(37 99 235 / 0.45);
}
.doc-cta:active:not(:disabled) {
  transform: translateY(0) scale(0.98);
}
.doc-cta:disabled {
  background: #e2e8f0;
  color: #94a3b8;
  cursor: not-allowed;
}
.doc-cta i {
  font-size: 14px;
}
.doc-cta-hint {
  font-size: 12px;
  color: #94a3b8;
}

.doc-progress-wrap {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.doc-progress {
  position: relative;
  height: 2px;
  width: 100%;
  border-radius: 2px;
  overflow: hidden;
  background: #e2e8f0;
}
.doc-progress::after {
  content: '';
  position: absolute;
  inset: 0;
  width: 38%;
  border-radius: 2px;
  background: #2563eb;
  animation: doc-progress-slide 1.25s ease-in-out infinite;
}
@keyframes doc-progress-slide {
  0% {
    transform: translateX(-120%);
  }
  100% {
    transform: translateX(320%);
  }
}
.doc-progress-text {
  font-size: 12px;
  color: #475569;
}

.doc-files {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: 16px;
  border-top: 1px solid #f1f5f9;
}
.doc-file-list {
  display: flex;
  flex-direction: column;
}
.doc-file-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 0;
  border-top: 1px solid #f1f5f9;
}
.doc-file-row:first-child {
  border-top: 0;
}
.doc-file-name {
  font-size: 14px;
  color: #1e293b;
}
.doc-file-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
}
.doc-file-link {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #94a3b8;
  text-decoration: none;
  transition: color 0.18s ease;
}
.doc-file-link:hover {
  color: #0f172a;
}
.doc-file-link.is-download {
  color: #475569;
  font-weight: 500;
}
.doc-file-sep {
  width: 1px;
  height: 12px;
  background: #e2e8f0;
}
.doc-folder-link {
  align-self: flex-start;
  margin-top: 2px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #94a3b8;
  text-decoration: none;
  transition: color 0.18s ease;
}
.doc-folder-link:hover {
  color: #0f172a;
}

.job-content-text {
  font-family: 'Sarabun', sans-serif;
  line-height: 1.7;
  letter-spacing: 0.01em;
}
</style>
